/**
 * EditarBajaDialog — la fecha y el motivo de una baja abierta.
 *
 * ── POR QUÉ EXISTE COMO COMPONENTE ────────────────────────────────────────────────────────────
 *
 * Esto vivía EMBEBIDO en la ficha desde Fase 14, y el panel del proceso —que es donde se buscó—
 * no lo tenía. Copiarlo allí habría dejado dos diálogos que avisan del mismo acto, y ya se ha
 * visto adónde lleva eso: el aviso de crear una baja decía la verdad en una pantalla y la
 * creencia de hace dos fases en la siguiente.
 *
 * Se extrae tal cual, sin cambiarle el comportamiento: mismos textos, mismo aviso, misma llamada.
 * Lo único que cambia es que ahora hay UN sitio donde tocarlo.
 *
 * ── LA FECHA DECIDE QUIÉN PUEDE ENTRAR ────────────────────────────────────────────────────────
 *
 * El motivo es un id del catálogo —dejó de ser texto el 01/10 y la columna se retiró el 02/10—.
 * La FECHA da o quita el acceso a una persona, y por eso este diálogo avisa
 * ANTES de guardar y no después. Es la misma razón que en el de editar el alta.
 *
 * El aviso PREVIO es una previsión —compara la fecha vieja con la nueva—; el HECHO lo cuenta el
 * servidor en `efecto.accion`, y es ése el que se dice al terminar.
 */
import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { Modal } from '../ui-v2.5';
import { editarBaja, listMotivosBaja } from './api';

/**
 * Si esa fecha AAAA-MM-DD ya llegó. Comparar cadenas ordena igual que comparar fechas.
 *
 * En **Europe/Madrid** y no en la zona del navegador: es la zona que usa `hoyMadrid` en el
 * servidor, que es quien de verdad decide. Con la zona local, alguien mirando desde otro huso
 * vería un aviso distinto del efecto que va a ocurrir.
 */
const yaLlegada = (iso) => {
  if (!iso) return false;
  const hoy = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Europe/Madrid', year: 'numeric', month: '2-digit', day: '2-digit',
  }).format(new Date());
  return String(iso).slice(0, 10) <= hoy;
};

export default function EditarBajaDialog({ open, baja, personaNombre, onClose, onGuardada }) {
  const [fecha, setFecha] = useState('');
  const [motivoId, setMotivoId] = useState('');
  const [motivos, setMotivos] = useState([]);
  /* ── «FALLO» Y «VACIO» NO SE PUEDEN VER IGUAL ────────────────────────────────────────────
   * Aqui habia un `.catch(() => setMotivos([]))`. Con el, una peticion rechazada dejaba el
   * desplegable con solo «Seleccionar…» — exactamente igual que si el catalogo estuviera vacio.
   * Costo media hora de diagnostico en produccion, y lo escribi el MISMO dia en que estabamos
   * arreglando este mismo patron en el formulario de crear personas.
   * Ahora el error se guarda y se dice, y el vacio dice otra cosa. */
  const [errorMotivos, setErrorMotivos] = useState(null);
  const [guardando, setGuardando] = useState(false);

  useEffect(() => {
    if (!open || !baja) return;
    setFecha(baja.fecha_baja ? String(baja.fecha_baja).slice(0, 10) : '');
    setMotivoId(baja.motivo_id ? String(baja.motivo_id) : '');
    setGuardando(false);
    /* El catalogo se pide al ABRIR y no al montar: este dialogo vive montado en dos pantallas y
     * pedirlo siempre seria una consulta por cada ficha que alguien mira. */
    setErrorMotivos(null);
    listMotivosBaja()
      .then((d) => setMotivos((d && d.motivos) || []))
      .catch((e) => { setMotivos([]); setErrorMotivos((e && e.response && e.response.data && e.response.data.message) || 'No se ha podido cargar el catálogo de motivos. Vuelve a abrir el diálogo.'); });
  }, [open, baja]);

  if (!open || !baja) return null;

  const nombre = personaNombre || 'Esta persona';
  const original = baja.fecha_baja ? String(baja.fecha_baja).slice(0, 10) : '';

  /* ── UNA BAJA CERRADA ADMITE CAMBIAR EL MOTIVO, Y SOLO ESO ───────────────────────────────
   *
   * Corregir el motivo arregla un DATO; mover la fecha mueve a una PERSONA —le da o le quita el
   * acceso—. Son dos cosas distintas bajo el mismo boton, asi que no se abren juntas: el servidor
   * lo rechaza igual (409 `ya_completada` si llega una fecha), y aqui el campo sale bloqueado con
   * el motivo escrito en vez de en gris sin explicacion.
   *
   * Hizo falta de verdad: habia una baja CERRADA con el motivo sin mapear, y si solo se pudiera
   * tocar en las abiertas esa fila habria quedado pendiente para siempre —y la migracion que
   * retira la columna no habria podido completarse nunca—. Ya no quedan pendientes, pero la regla
   * se queda: corregir el motivo de una baja cerrada sigue siendo arreglar un dato. */
  const cerrada = baja.estado === 'completada';

  /* Si el motivo actual esta DESACTIVADO, se anade a la lista para que no desaparezca del
   * desplegable al abrirlo: si no, editar la fecha de esa baja le cambiaria el motivo sin querer. */
  const opciones = (() => {
    const activos = motivos.filter((m) => m.activo);
    const actual = motivos.find((m) => String(m.id) === String(baja.motivo_id));
    return actual && !actual.activo ? [actual, ...activos] : activos;
  })();

  /* ── EL AVISO QUE ES EL PUNTO DE LA FASE ─────────────────────────────────────────────────
   *
   * **Cambiar una fecha no parece una acción que quite el acceso a nadie, y lo es.** Quien mueve
   * la fecha de una baja está pensando en la fecha, no en el acceso — y si no se lo dice nadie,
   * se entera cuando esa persona llama diciendo que no puede entrar.
   *
   * Se avisa sólo si la fecha CRUZA la línea de hoy: de futura a pasada, o al revés. Mover una
   * fecha pasada a otra pasada no cambia nada, y avisar de lo que no pasa enseña a no leer los
   * avisos.
   *
   * Con el NOMBRE de la persona: «alguien» no es nadie. */
  const aviso = (() => {
    const antes = yaLlegada(original);
    const despues = yaLlegada(fecha);
    if (antes === despues) return null;
    return despues
      ? { tono: 'danger', texto: `Al guardar, ${nombre} PERDERÁ el acceso a la intranet: la fecha pasa a ser de hoy o anterior.` }
      : { tono: 'ok', texto: `Al guardar, ${nombre} RECUPERARÁ el acceso a la intranet, con la situación que tenía antes de la baja.` };
  })();

  const guardar = async () => {
    setGuardando(true);
    try {
      /* En una cerrada NO se manda la fecha. Mandarla «igual que estaba» tambien seria mandarla,
       * y el servidor la rechazaria con un 409 que no explicaria nada a quien solo tocaba el
       * motivo. */
      const payload = cerrada
        ? { motivo_id: Number(motivoId) }
        : { fecha_baja: fecha, motivo_id: Number(motivoId) };
      const r = await editarBaja(baja.id, payload);
      // Lo que le pasó a la persona se DICE, no se supone: es el dato por el que este diálogo
      // existe. Y sale de `efecto`, del servidor — no de comparar fechas aquí.
      const acc = r && r.efecto && r.efecto.accion;
      if (acc === 'desactivada') toast.warn(`Guardado. ${nombre} ha perdido el acceso a la intranet.`, { autoClose: 8000 });
      else if (acc === 'reactivada') toast.success(`Guardado. ${nombre} recupera el acceso a la intranet.`, { autoClose: 8000 });
      else toast.success('Proceso de salida actualizado.');
      onGuardada && onGuardada(r);
      onClose && onClose();
    } catch (e) {
      toast.error((e && e.response && e.response.data && e.response.data.message) || 'No se ha podido guardar.');
      setGuardando(false);
    }
  };

  return (
    <Modal
      open
      onClose={guardando ? undefined : onClose}
      size="sm"
      title="Editar el proceso de salida"
      subtitle={`${baja.numero || 'Baja'} · ${nombre}`}
      footer={(
        <div className="flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={guardando}
            className="rounded-v25-md border border-v25-line bg-white px-4 py-2 text-[13px] font-semibold text-v25-ink-700 shadow-v25-btn disabled:opacity-50"
          >
            Cancelar
          </button>
          <button
            type="button"
            disabled={guardando || !motivoId || (!cerrada && !fecha)}
            onClick={guardar}
            className="rounded-v25-md bg-v25-ink-900 px-4 py-2 text-[13px] font-semibold text-white shadow-v25-btn disabled:opacity-50"
          >
            {guardando ? 'Guardando…' : 'Guardar'}
          </button>
        </div>
      )}
    >
      <div className="space-y-3">
        <div>
          <label className="mb-1 block text-[12px] font-semibold text-v25-ink-700" htmlFor="baja-fecha">Fecha de baja</label>
          <input
            id="baja-fecha"
            type="date"
            value={fecha}
            disabled={cerrada}
            onChange={(ev) => setFecha(ev.target.value)}
            className="w-full rounded-v25-md border border-v25-line px-3 py-2 text-[13px] disabled:bg-v25-ink-50 disabled:text-v25-ink-400"
          />
          {cerrada && (
            <p className="mt-1 text-[12px] text-v25-ink-500">
              La baja está cerrada: la fecha ya no se puede cambiar porque decide el acceso de la
              persona. El motivo sí.
            </p>
          )}
        </div>
        <div>
          <label className="mb-1 block text-[12px] font-semibold text-v25-ink-700" htmlFor="baja-motivo">Motivo</label>
          <select
            id="baja-motivo"
            value={motivoId}
            onChange={(ev) => setMotivoId(ev.target.value)}
            className="w-full rounded-v25-md border border-v25-line px-3 py-2 text-[13px]"
          >
            <option value="">Seleccionar…</option>
            {opciones.map((m) => (
              <option key={m.id} value={m.id}>{m.nombre}{m.activo ? '' : ' (desactivado)'}</option>
            ))}
          </select>
          {/* LAS DOS CAUSAS, DICHAS APARTE. Sin esto, el desplegable vacio es un misterio. */}
          {errorMotivos && (
            <p className="mt-1 text-[12px] leading-relaxed text-red-600">{errorMotivos}</p>
          )}
          {!errorMotivos && motivos.length === 0 && (
            <p className="mt-1 text-[12px] leading-relaxed text-amber-800">
              No hay motivos en el catálogo. Créalos en <strong>Configuración → Motivos de baja</strong>.
            </p>
          )}
          {/* AQUI HABIA el aviso del motivo «sin mapear», con el texto antiguo a la vista. Existio
              para que alguien fuera a resolverlo, y cumplio: el 02/10 se completo la conversion y
              se retiro `incorp_bajas.motivo`. Desde entonces `baja.motivo` es `undefined` siempre,
              asi que ese bloque no podia pintarse nunca —y dejarlo haria creer que el caso sigue
              existiendo, que es como un comentario sobrevive a su comportamiento. */}
        </div>
        {aviso && (
          <div className={`rounded-v25-md border p-3 text-[13px] leading-relaxed ${aviso.tono === 'danger'
            ? 'border-v25-expired-bd bg-v25-expired-bg text-v25-expired-fg'
            : 'border-v25-line bg-v25-ok-bg text-v25-ink-900'}`}
          >
            <strong>{aviso.tono === 'danger' ? 'Ojo: ' : ''}</strong>{aviso.texto}
          </div>
        )}
      </div>
    </Modal>
  );
}
